import { Tooltip } from '@mui/material'
import { createMRTColumnHelper } from 'material-react-table'
import { ReactNode } from 'react'

import { CheckboxCell, SelectOption } from '@/components'
import {
	isCloseShortcut,
	isEditableElement,
	isSaveShortcut,
	isTextEditingKey,
} from '@/hooks/useKeyboardNavigation'

import {
	type MrtCellProps,
	type OnKeyDownHandlerParams,
	type OnTextFieldModalContentKeyDown,
} from './types'

const columnHelper = createMRTColumnHelper()

const MODAL_TRIGGER_KEY = 'Enter'

export const onKeyDownHandler = ({
	columnId,
	columnName,
	elementLabel,
	event,
	setModalProps,
	rowIndex,
	textValue,
}: OnKeyDownHandlerParams) => {
	if (rowIndex === undefined || rowIndex === null) return
	if (columnId === undefined || columnId === null) return

	// Stop navigation keys from propagating when in editable elements
	if (isEditableElement(event.target) && isTextEditingKey(event.key)) {
		event.stopPropagation()
	}

	// Alt+Enter opens modal dialog
	if (event.altKey && event.key === MODAL_TRIGGER_KEY && setModalProps) {
		return setModalProps?.({
			columnId,
			columnName,
			elementLabel,
			isModalOpen: true,
			value: textValue,
			rowIndex,
		})
	}
}

export const textCellKeyDownHandler = (
	event: React.KeyboardEvent<HTMLInputElement>,
) => {
	// Stop navigation keys from propagating when in editable elements
	if (isEditableElement(event.target) && isTextEditingKey(event.key)) {
		event.stopPropagation()
	}
}

// Keyboard shortcuts for modal dialogs
export const onTextFieldModalContentKeyDown = ({
	event,
	handleSave,
	handleClose,
}: OnTextFieldModalContentKeyDown) => {
	// Stop navigation keys from propagating when in editable elements
	if (isEditableElement(event.target) && isTextEditingKey(event.key)) {
		event.stopPropagation()
	}

	// Ctrl+Enter or Cmd+Enter to save
	if (isSaveShortcut(event)) {
		event.preventDefault()
		event.stopPropagation()
		handleSave()
		return
	}

	// Escape to close
	if (isCloseShortcut(event)) {
		event.stopPropagation()
		handleClose()
	}
}

export const createCheckboxCell = ({
	accessor,
	header,
	size,
	isReadOnly = false,
}: MrtCellProps<Record<string, unknown>>) =>
	columnHelper.accessor(String(accessor), {
		...createHeaderWithTooltip(header),
		size: size,
		Cell: ({ cell, table, row, column }) => (
			<CheckboxCell
				isDisabled={isReadOnly}
				cell={cell}
				column={column}
				table={table}
				row={row}
			/>
		),
	})

export const createHeaderWithTooltip = (headerValue: string | undefined) => {
	return {
		header: headerValue || 'MISSING HEADER',
		Header: () => (
			<Tooltip title={headerValue} arrow>
				<span>{headerValue}</span>
			</Tooltip>
		),
	}
}

export const createRequiredHeaderWithTooltip = (headerValue: string | undefined) => {
	return {
		header: headerValue || 'MISSING HEADER',
		Header: () => (
			<Tooltip title={`${headerValue} (required)`} arrow>
				<span>
					{headerValue}
					<span style={{ color: '#d32f2f', marginLeft: 2 }}>*</span>
				</span>
			</Tooltip>
		),
	}
}

export { createOptionColumnFilterFn } from './utils/optionColumnFilter'

export const getSelectOptionLabel = (
	option: SelectOption,
	optionLabel: string,
): string | ReactNode => {
	const label = `${option[optionLabel]}${option.wasDeleted ? ' !' : ''}`

	if (option.fullPath && typeof option.fullPath !== 'number') {
		const name = option[optionLabel]
		const fullPath = option.fullPath

		const trimmedFullPath = fullPath.includes('>')
			? fullPath.substring(0, fullPath.lastIndexOf('>'))
			: fullPath

		return (
			<span>
				{name}
				<span className="text-xs text-gray-500 ml-1">({trimmedFullPath})</span>
			</span>
		)
	}

	return label
}
